// react
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// material ui
import { Box, Card, CardContent, CircularProgress, Stack } from '@mui/material';

// api
import type {
  AccountsPostAsyncBynewAccountEnhancedResponse,
  ReadAccountDto,
} from '@/api/generated/recalc/endpoints/v1/accounts.enhanced';
import {
  useAccountsGetCollectionAsyncByqueryOptionsQuery,
  useAccountsPostAsyncBynewAccountMutation,
  useAccountsPutServiceAsyncByaccountIdserviceIdMutation,
} from '@/api/generated/recalc/endpoints/v1/accounts.enhanced';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFTextField } from '@/components/ui/form';

// components
import { setSelectedAccount } from '@/redux/selectedAccountSlice';
import { servicesList } from '@/utils/services';
import { withAccount } from '@/utils/withAccount';

// relative
import {
  toCreateAccountDto,
  toCreateAccountFormValues,
} from './createAccountForm.mappers';
import { createAccountFormSchema } from './createAccountForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';

export default function CreateAccountForm({
  onCancel,
}: {
  onCancel: () => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);

  const dispatch = useDispatch();

  const { data: allAccounts } =
    useAccountsGetCollectionAsyncByqueryOptionsQuery({
      orderby: 'AccountId',
      top: 10000,
    });

  const [createAccount, { isLoading: isCreating }] =
    useAccountsPostAsyncBynewAccountMutation();

  const [addServiceToAccount] =
    useAccountsPutServiceAsyncByaccountIdserviceIdMutation();

  const schema = createAccountFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toCreateAccountFormValues(),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    const newAccount = toCreateAccountDto(values);
    // `accountsPostAsyncBynewAccount`'s `transformResponse` is patched in
    // `accounts.enhanced.ts` to parse the created account id out of the
    // response's `Location` header - cast to that runtime shape here since
    // the codegen's own response type (`object`) can't express it.
    let createdAccountId: string | number | null | undefined;
    try {
      ({ accountId: createdAccountId } = (await createAccount({
        writeAccountDto: newAccount,
      }).unwrap()) as AccountsPostAsyncBynewAccountEnhancedResponse);
    } catch (error) {
      // Error already surfaced by apiErrorMiddleware (snackbar + App
      // Insights) - stop here so we don't provision services or redirect
      // for an account that was never created, and so `.unwrap()`'s
      // rejection doesn't become an unhandled promise rejection.
      console.error('Error creating account:', error);
      return;
    }

    const defaultServiceNames = ['Devices', 'Alarms', 'Analytics'];
    const defaultServiceIds = servicesList
      .filter((service) => defaultServiceNames.includes(service.Name))
      .map((service) => service.Id);

    if (createdAccountId) {
      try {
        await Promise.all(
          defaultServiceIds.map((serviceId) =>
            addServiceToAccount({
              accountId: Number(createdAccountId),
              serviceId,
            }).unwrap(),
          ),
        );
      } catch (error) {
        console.error('Error adding default services:', error);
      }
    }

    // TODO: Conside having smore more robust error handling here
    if (createdAccountId) {
      // Redirect to account settings page. One-off browser navigation on
      // submit, not a render-time mutation - safe to opt out of the
      // compiler's immutability check here.
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = `/auth/silent-login?returnTo=${encodeURIComponent(
        withAccount(Number(createdAccountId), 'account/profile'),
      )}`;
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  // Handle cookie-based account selection after redirect
  useEffect(() => {
    const match = document.cookie.match(/localhost_GuiState=AccountId=(\d+)/);
    const guiStateAccountId = match ? match[1] : null;

    if (!guiStateAccountId || !allAccounts?.length) return;

    const selectedAccount = allAccounts.find(
      (acc: ReadAccountDto) =>
        String(acc.AccountId) === String(guiStateAccountId),
    );

    if (selectedAccount) {
      dispatch(setSelectedAccount(selectedAccount));
      // Clear the cookie
      document.cookie =
        'localhost_GuiState=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  }, [allAccounts, dispatch]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                label={t('createAccount.name.label', { ns: 'forms' })}
                placeholder={t('createAccount.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createAccount.name.helperText', {
                  ns: 'forms',
                })}
                inputProps={{ 'data-testid': 'account-name-input' }}
                required
                fullWidth
              />

              <RHFTextField
                name="address"
                control={control}
                label={t('createAccount.address.label', { ns: 'forms' })}
                placeholder={t('createAccount.address.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createAccount.address.helperText', {
                  ns: 'forms',
                })}
                inputProps={{ 'data-testid': 'account-address-input' }}
                required
                fullWidth
              />

              <RHFTextField
                name="billingAddress"
                control={control}
                label={t('createAccount.billingAddress.label', { ns: 'forms' })}
                placeholder={t('createAccount.billingAddress.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createAccount.billingAddress.helperText', {
                  ns: 'forms',
                })}
                fullWidth
                inputProps={{ 'data-testid': 'account-billing-input' }}
              />
            </Stack>
          </CardContent>
        </Card>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isCreating}
            data-testid="account-cancel-button"
          >
            {t('common.cancel')}
          </CancelButton>
          <SubmitButton
            disabled={isCreating || !isValid}
            data-testid="account-save-button"
            startIcon={
              isCreating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddRoundedIcon />
              )
            }
          >
            {isCreating
              ? t('createAccount.submitting', { ns: 'forms' })
              : t('createAccount.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
